import React, { useRef, useMemo } from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { Formik, Form, FormikProps } from 'formik'
import { Column } from '@/components/layout/layout'
import Button from '@/components/button/button'
import { layerManage } from '@/utils/layer'
import Input from '@/components/input/input'
import { checkError, getErrorStr } from '@/utils/form'
import { useRequest } from 'ahooks'
import { getBEMCls } from '@/utils/classname'
import FormItem from '../../../../components/form/form-item/form-item'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { goodsLibraryStore } from '../../store/goods-library'
import { Modal } from '@/components/dialog/modal'

interface IGroupForm {
    name: string
}

interface IGroupFormError {
    name?: string
}

interface EditGroupProps {
    groupId?: number
    secondId?: number
    name?: string
    mode?: 'add' | 'edit'
}

const EditGroup: React.FC<EditGroupProps> = props => {
    const cls = (e?: string, m?: string) => getBEMCls('goods-group', e, m)
    const formRef = useRef<FormikProps<IGroupForm>>(null)

    const initVal: IGroupForm = {
        name: props.name || '',
    }

    const createReq = useRequest(
        (val: IGroupForm) =>
            $post({
                url:
                    props.mode === 'edit'
                        ? props.secondId
                            ? '/goods/editsecondarygroup'
                            : '/goods/editgroup'
                        : props.groupId
                        ? '/goods/addsecondarygroup'
                        : '/goods/addgroup',
                params: {
                    ...val,
                    group_id: props.groupId,
                    secondary_group_id: props.secondId,
                    delete: false,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                goodsLibraryStore.setUpdateGroup()
                layerManage.pop()
                $msg('success', `${props.mode === 'edit' ? '更新' : '添加'}成功`)
            },
            onError: e => $msg('error', e.message),
        },
    )

    const onSubmit = (val: IGroupForm) => {
        createReq.run(val)
    }

    const validateVal = (val: IGroupForm) => {
        const error: IGroupFormError = {}

        if (!val.name.trim().length) {
            error.name = '请输入分组名称'
        }

        return error
    }

    const getIptLen = useMemo(() => {
        if (props.secondId || (props.mode === 'add' && props.groupId)) {
            return 5
        } else {
            return 10
        }
    }, [props.groupId, props.mode, props.secondId])

    return (
        <BaseDialog
            isShow
            title={props.mode === 'edit' ? '编辑分组' : '添加分组'}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Formik
                onSubmit={onSubmit}
                validateOnBlur
                initialValues={initVal}
                validate={validateVal}
                innerRef={formRef}
            >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                    <Form>
                        <Column className={cls('form-container')}>
                            <FormItem
                                titleWidth={72}
                                isRequire
                                title="分组名称"
                                error={getErrorStr('name', errors, touched)}
                            >
                                <Input
                                    error={checkError('name', errors, touched)}
                                    name="name"
                                    maxLength={getIptLen}
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="请输入分组名称"
                                    inputsuffix={() => (
                                        <span data-font-size-14 data-color-000>
                                            {values.name.length}/{getIptLen}
                                        </span>
                                    )}
                                />
                            </FormItem>
                            <div className={cls('footer')}>
                                <div
                                    data-cursor-pointer
                                    className={cls('footer', 'cancel')}
                                    onClick={() => {
                                        layerManage.pop()
                                    }}
                                >
                                    取消
                                </div>
                                <Button loading={createReq.loading || void 0} types="default" type="submit">
                                    确定
                                </Button>
                            </div>
                        </Column>
                    </Form>
                )}
            </Formik>
        </BaseDialog>
    )
}

interface DeleteGroupProps {
    name: string
    num: number
    groupId?: number
    secondId?: number
}

export const DeleteGroup: React.FC<DeleteGroupProps> = props => {
    const getCls = (e?: string, m?: string) => getBEMCls('goods-group', e, m)

    const popDialog = () => layerManage.pop()

    const { run } = useRequest(
        () =>
            $post({
                url: '/goods/deletegroup',
                params: {
                    group_id: props.groupId,
                    secondary_group_id: props.secondId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '删除成功')
                layerManage.pop()
                goodsLibraryStore.setUpdateGroup()
            },
            onError: e => $msg('error', e.message),
        },
    )

    return (
        <Modal type="danger" isShow onClose={popDialog} onCancel={popDialog} onConfirm={run}>
            <div className={`${getCls('del')}`}>
                <div className={`${getCls('del', 'title')}`}>
                    确认删除分组<div className={`${getCls('del', 'name')}`}>{props.name}</div>吗？
                </div>
                <div className={`${getCls('del', 'des')}`}>分组内包含商品{props.num}种，删除后商品将被放入回收站</div>
            </div>
        </Modal>
    )
}

EditGroup.defaultProps = {
    mode: 'edit',
}

export default EditGroup
